import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { AuthControllerApi } from '../api-generated';
import { apiConfig } from '../api-generated/apiConfig';
import { AUTH_UNAUTHORIZED_EVENT } from './authEvents';

interface AuthState {
  token: string | null;
  role: string | null;
  email: string | null;
  isAuthenticated: boolean;
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType>({
  token: null,
  role: null,
  email: null,
  isAuthenticated: false,
  login: async () => {},
  logout: () => {},
});

export const useAuth = () => useContext(AuthContext);

// Read an auth value from whichever store holds the session (localStorage = "remembered",
// sessionStorage = current session only).
function readAuth(key: string): string | null {
  return localStorage.getItem(key) ?? sessionStorage.getItem(key);
}

// Clear an auth value from both stores.
function clearAuth(key: string): void {
  localStorage.removeItem(key);
  sessionStorage.removeItem(key);
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [auth, setAuth] = useState<AuthState>(() => {
    const token = readAuth('token');
    const role = readAuth('role');
    const email = readAuth('email');
    return { token, role, email, isAuthenticated: !!token };
  });

  const login = useCallback(async (email: string, password: string, rememberMe = false) => {
    const authApi = new AuthControllerApi(apiConfig);
    const data = await authApi.login({ requestBody: { email, password } });

    // Persist to localStorage when "remembered" (survives browser restart) or sessionStorage
    // otherwise (cleared when the tab/browser closes). Clear the other store to avoid staleness.
    const store = rememberMe ? window.localStorage : window.sessionStorage;
    const otherStore = rememberMe ? window.sessionStorage : window.localStorage;
    ['token', 'role', 'email', 'accessToken'].forEach((k) => otherStore.removeItem(k));

    store.setItem('token', data.token);
    store.setItem('role', data.role);
    store.setItem('email', email);
    if ((data as any).accessToken) store.setItem('accessToken', (data as any).accessToken);
    setAuth({ token: data.token, role: data.role, email, isAuthenticated: true });
  }, []);

  const logout = useCallback(() => {
    clearAuth('token');
    clearAuth('role');
    clearAuth('email');
    clearAuth('accessToken');
    setAuth({ token: null, role: null, email: null, isAuthenticated: false });
  }, []);

  // When any API call reports an expired/invalid session (see authEvents), log out so the route
  // guard redirects to /login instead of leaving the user on a blank, non-loading page.
  useEffect(() => {
    const onUnauthorized = () => logout();
    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized);
  }, [logout]);

  return (
    <AuthContext.Provider value={{ ...auth, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
